/**
 * fft-pure — the CPU-side math behind the FFT ocean, kept free of three.js
 * so every piece is unit-testable in Node:
 *
 *   - 2-D spectral amplitudes |h̃0(k)| for the same families the Gerstner
 *     sampler uses (Phillips / Pierson–Moskowitz / JONSWAP, finite depth)
 *   - deterministic Hermitian-packed h0 grid generation (seeded Box–Muller)
 *   - Cooley–Tukey radix-2 BUTTERFLY TABLES (bit-reversal + twiddles) that
 *     drive the GPU passes — validated here against a direct DFT
 *   - a CPU emulation of the exact butterfly algorithm the GPU runs
 *   - the buoyancy mirror: the top-energy modes of the SAME h0 grid,
 *     converted to Gerstner components for CPU height queries
 *
 * Conventions: texel (x, y) ↔ wavevector k = 2π(x − N/2, y − N/2)/L, IFFT
 * sign e^{+i k·x}, and the (−1)^(x+y) permutation after the passes recenters
 * the spectrum (standard Tessendorf pipeline).
 */

import type { GerstnerWave, SpectrumWaveSetConfig } from '../GerstnerWaves.js';

const GRAVITY = 9.81;
const TAU = Math.PI * 2;

export type OceanSpectrumConfig = Omit<SpectrumWaveSetConfig, 'swell' | 'count' | 'steepness'>;

function lcg(state: number): number {
  return (state * 1664525 + 1013904223) >>> 0;
}

/** Finite-depth dispersion ω² = gk·tanh(kD) (deep water when D = ∞). */
export function dispersionOmega(k: number, depth: number | undefined, g = GRAVITY): number {
  return depth !== undefined && Number.isFinite(depth)
    ? Math.sqrt(g * k * Math.tanh(k * depth))
    : Math.sqrt(g * k);
}

/**
 * 2-D variance density S(kx, kz) [m²·m²] such that a mode's amplitude is
 * |h̃0| = √(S·Δkx·Δkz / 2) · gaussian. Directional cos² spread around the
 * wind; the ω-space families convert with dω/dk and the polar 1/k Jacobian.
 */
export function spectralDensity2D(kx: number, kz: number, cfg: OceanSpectrumConfig): number {
  const k = Math.hypot(kx, kz);
  if (k < 1e-6) return 0;
  const g = cfg.gravity ?? GRAVITY;
  const V = Math.max(cfg.windSpeed ?? 8, 0.5);
  const windRad = ((cfg.windDirectionDeg ?? 25) * Math.PI) / 180;
  const model = cfg.model ?? 'phillips';
  const depth = cfg.depth;

  // Directional spread |k̂·ŵ|² with the back-travelling half suppressed.
  const dotW = (kx / k) * Math.cos(windRad) + (kz / k) * Math.sin(windRad);
  const directional = dotW > 0 ? dotW * dotW : dotW * dotW * 0.15;

  if (model === 'phillips') {
    const L = (V * V) / g;
    const l = L / 1000;
    const ALPHA = 0.0081;
    const pk = (ALPHA * Math.exp(-1 / (k * L) ** 2) * Math.exp(-(k * k) * (l * l))) / k ** 4;
    return pk * directional;
  }

  // ω-space families → 2-D k-plane: S2D = S(ω)·(dω/dk)/k · D(θ) (+ TMA).
  const w = dispersionOmega(k, depth, g);
  let dwdk: number;
  if (depth !== undefined && Number.isFinite(depth)) {
    const th = Math.tanh(k * depth);
    dwdk = (g * (th + k * depth * (1 - th * th))) / (2 * w);
  } else {
    dwdk = g / (2 * w);
  }
  let sOmega: number;
  if (model === 'jonswap') {
    const fetch = Math.max(1, cfg.fetchKm ?? 100) * 1000;
    const gamma = cfg.gamma ?? 3.3;
    const alpha = 0.076 * Math.pow((V * V) / (fetch * g), 0.22);
    const wp = 22 * Math.pow((g * g) / (V * fetch), 1 / 3);
    const sigma = w <= wp ? 0.07 : 0.09;
    const r = Math.exp(-((w - wp) ** 2) / (2 * sigma * sigma * wp * wp));
    sOmega = ((alpha * g * g) / w ** 5) * Math.exp(-1.25 * (wp / w) ** 4) * Math.pow(gamma, r);
  } else {
    const wp = (0.855 * g) / V;
    sOmega = ((0.0081 * g * g) / w ** 5) * Math.exp(-1.25 * (wp / w) ** 4);
  }
  let tma = 1;
  if (depth !== undefined && Number.isFinite(depth)) {
    const wh = w * Math.sqrt(depth / g);
    tma = wh <= 1 ? 0.5 * wh * wh : wh < 2 ? 1 - 0.5 * (2 - wh) * (2 - wh) : 1;
  }
  return ((sOmega * tma * dwdk) / k) * directional;
}

/**
 * Deterministic h0 grid: RGBA texel (x, y) = [h0(k).re, h0(k).im,
 * h0(−k).re, h0(−k).im] — the evolve kernel needs both to keep h(k, t)
 * Hermitian (a REAL surface). Seeded Box–Muller, no Math.random.
 */
export function generateH0(N: number, patchSize: number, cfg: OceanSpectrumConfig, seed = 1789): Float32Array {
  const out = new Float32Array(N * N * 4);
  const dk = TAU / patchSize;
  // Gaussian per texel from a texel-indexed hash — h0(−k) must read the
  // SAME gaussians the −k texel got, so derive randomness from coordinates.
  const gaussianAt = (x: number, y: number): [number, number] => {
    let s = (seed ^ (x * 73856093) ^ (y * 19349663)) >>> 0;
    s = lcg(s);
    const u1 = Math.max(1e-9, s / 0xffffffff);
    s = lcg(s);
    const u2 = s / 0xffffffff;
    const r = Math.sqrt(-2 * Math.log(u1));
    return [r * Math.cos(TAU * u2), r * Math.sin(TAU * u2)];
  };
  const amp = (kx: number, kz: number): number =>
    Math.sqrt(Math.max(0, spectralDensity2D(kx, kz, cfg) * dk * dk) / 2);

  for (let y = 0; y < N; y++) {
    const kz = (y - N / 2) * dk;
    for (let x = 0; x < N; x++) {
      const kx = (x - N / 2) * dk;
      const [g1, g2] = gaussianAt(x, y);
      const a = amp(kx, kz);
      // −k texel (mirrored around N/2; the DC/Nyquist row maps onto itself).
      const mx = (N - x) % N === 0 ? 0 : N - x;
      const my = (N - y) % N === 0 ? 0 : N - y;
      const [g3, g4] = gaussianAt(mx === 0 ? 0 : mx, my === 0 ? 0 : my);
      const aM = amp(-kx, -kz);
      const o = (y * N + x) * 4;
      out[o] = g1 * a;
      out[o + 1] = g2 * a;
      out[o + 2] = g3 * aM;
      out[o + 3] = g4 * aM;
    }
  }
  return out;
}

// ─── Butterfly tables (Cooley–Tukey radix-2, decimation in time) ────────────

/** Bit-reverse `x` over `bits` bits. */
export function bitReverse(x: number, bits: number): number {
  let r = 0;
  for (let i = 0; i < bits; i++) {
    r = (r << 1) | ((x >> i) & 1);
  }
  return r;
}

/**
 * Butterfly table for an N-point IFFT: `stages = log2(N)` columns, each with
 * N rows of [twiddle.re, twiddle.im, indexA, indexB]. Stage 0 reads
 * bit-reversed input indices; later stages read the previous pass. The GPU
 * runs one pass per stage per axis; the CPU emulation below runs the same
 * table so tests pin the algorithm against a direct DFT.
 */
export function generateButterfly(N: number): { data: Float32Array; stages: number } {
  const stages = Math.log2(N);
  if (!Number.isInteger(stages)) throw new Error(`FFT size must be a power of two, got ${N}`);
  const data = new Float32Array(stages * N * 4);
  for (let stage = 0; stage < stages; stage++) {
    const span = 2 ** stage; // butterfly half-width
    const group = span * 2;
    for (let x = 0; x < N; x++) {
      const inGroup = x % group;
      const top = inGroup < span;
      const k = (x % span) * (N / group); // twiddle index
      const angle = (TAU * k) / N; // IFFT: e^{+iθ}
      const o = (stage * N + x) * 4;
      // Bottom wings NEGATE the twiddle so every lane runs the same formula
      // out = in[a] + W·in[b]  (top: a + W·b; bottom: a − W·b) — no branch
      // in the GPU kernel.
      const sign = top ? 1 : -1;
      data[o] = Math.cos(angle) * sign;
      data[o + 1] = Math.sin(angle) * sign;
      let a: number;
      let b: number;
      if (top) {
        a = x;
        b = x + span;
      } else {
        a = x - span;
        b = x;
      }
      if (stage === 0) {
        a = bitReverse(a, stages);
        b = bitReverse(b, stages);
      }
      data[o + 2] = a;
      data[o + 3] = b;
    }
  }
  return { data, stages };
}

/**
 * CPU emulation of ONE butterfly pass over a row of N complex values —
 * EXACTLY the branch-free formula the GPU kernel runs per texel:
 *   out = in[a] + W·in[b]   (bottom wings carry a pre-negated W)
 * (`input`/`output` are [re, im] pairs.)
 */
export function applyButterflyRow(
  input: Float32Array,
  output: Float32Array,
  butterfly: Float32Array,
  N: number,
  stage: number,
): void {
  for (let x = 0; x < N; x++) {
    const o = (stage * N + x) * 4;
    const wr = butterfly[o];
    const wi = butterfly[o + 1];
    const ia = butterfly[o + 2] * 2;
    const ib = butterfly[o + 3] * 2;
    output[x * 2] = input[ia] + (wr * input[ib] - wi * input[ib + 1]);
    output[x * 2 + 1] = input[ia + 1] + (wr * input[ib + 1] + wi * input[ib]);
  }
}

/** Full 1-D IFFT via the butterfly table (unnormalized, e^{+iθ} kernel). */
export function ifft1d(values: Float32Array, butterfly: Float32Array, N: number): Float32Array {
  const stages = Math.log2(N);
  let ping = values.slice();
  let pong = new Float32Array(N * 2);
  for (let stage = 0; stage < stages; stage++) {
    applyButterflyRow(ping, pong, butterfly, N, stage);
    const t = ping;
    ping = pong;
    pong = t;
  }
  return ping;
}

// ─── Buoyancy mirror ─────────────────────────────────────────────────────────

/**
 * The `count` highest-energy modes of an h0 grid as Gerstner components —
 * the CPU stand-in for the GPU surface so buoyancy/height queries track the
 * dominant swell without a readback. Amplitude 2·|h0| (the +k and −k modes
 * add for a real field), physical dispersion, zero steepness contribution
 * beyond normalization (the FFT surface's chop lives on the GPU).
 */
export function topModesToGerstner(
  h0: Float32Array,
  N: number,
  patchSize: number,
  count = 12,
  depth?: number,
): GerstnerWave[] {
  const dk = TAU / patchSize;
  const modes: Array<{ amp: number; kx: number; kz: number; k: number }> = [];
  for (let y = 0; y < N; y++) {
    const kz = (y - N / 2) * dk;
    for (let x = 0; x < N; x++) {
      const kx = (x - N / 2) * dk;
      const k = Math.hypot(kx, kz);
      if (k < 1e-6) continue;
      const o = (y * N + x) * 4;
      const amp = 2 * Math.hypot(h0[o], h0[o + 1]);
      if (amp > 1e-7) modes.push({ amp, kx, kz, k });
    }
  }
  modes.sort((a, b) => b.amp - a.amp);
  const picked = modes.slice(0, count);
  return picked.map((m) => {
    const w = dispersionOmega(m.k, depth);
    return {
      dirX: m.kx / m.k,
      dirZ: m.kz / m.k,
      amplitude: m.amp,
      wavelength: TAU / m.k,
      q: 0, // height-only mirror: no horizontal chop on the CPU side
      w: m.k,  // spatial wavenumber — matches the GPU evolve kernel's kx/kz
      phi: w,  // ω(k) — the temporal rate (omegaTex on the GPU)
    };
  });
}
